import { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'sl' : 'en');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center pl-6">
            <button
              onClick={() => scrollToSection('home')}
              className={`flex items-center space-x-2 text-2xl font-bold transition-colors ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}
            >
              <img 
                src="/assets/fresh.png" 
                alt="Logo" 
                className="h-8 w-auto rounded-full" 
              />
              <span>
                <span className="font-light">Zavod</span>
                <span className="font-bold">DPUI</span>
              </span>
            </button>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('home')}
              className={`transition-colors hover:text-blue-600 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              {t('nav_home')}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className={`transition-colors hover:text-blue-600 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              {t('nav_about')}
            </button>
            <button
              onClick={() => scrollToSection('research')}
              className={`transition-colors hover:text-blue-600 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              {t('nav_research')}
            </button>
            <button
              onClick={() => scrollToSection('news')}
              className={`transition-colors hover:text-blue-600 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              {t('nav_news')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`transition-colors hover:text-blue-600 ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
            >
              {t('nav_contact')}
            </button>
            <button
              onClick={toggleLanguage}
              className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-all hover:bg-blue-50 ${
                isScrolled ? 'text-gray-700' : 'text-white hover:bg-white/10'
              }`}
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium uppercase">{language}</span>
            </button>
          </div>

          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 ${isScrolled ? 'text-gray-700' : 'text-white'}`}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-white shadow-lg">
          <div className="px-4 pt-2 pb-4 space-y-2">
            <button
              onClick={() => scrollToSection('home')}
              className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-blue-50 rounded-lg"
            >
              {t('nav_home')}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-blue-50 rounded-lg"
            >
              {t('nav_about')}
            </button>
            <button
              onClick={() => scrollToSection('research')}
              className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-blue-50 rounded-lg"
            >
              {t('nav_research')}
            </button>
            <button
              onClick={() => scrollToSection('news')}
              className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-blue-50 rounded-lg"
            >
              {t('nav_news')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-blue-50 rounded-lg"
            >
              {t('nav_contact')}
            </button>
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 w-full px-3 py-2 text-gray-700 hover:bg-blue-50 rounded-lg"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium uppercase">{language}</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
